import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Users, ChevronDown, ChevronRight } from "lucide-react";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";
const availTone = (a) => (a == null ? "text-muted-foreground" : a >= 95 ? "text-emerald-700" : a >= 80 ? "text-amber-700" : "text-red-600");

function FillBar({ availability }) {
  const v = availability == null ? 0 : Math.min(availability, 100);
  const color = availability == null ? "#cbd5e1" : availability >= 95 ? "#0d765a" : availability >= 80 ? "#b45309" : "#dc2626";
  return <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v}%`, background: color }} /></div>;
}

function Detail({ w }) {
  return (
    <tr className="bg-muted/20 border-b" data-testid={`svc-mp-detail-${w.code}`}>
      <td colSpan={7} className="px-6 py-3">
        <div className="grid gap-4 lg:grid-cols-3">
          {w.departments.map((d) => (
            <div key={d.dept} className="border bg-card rounded-md">
              <div className="px-3 py-2 border-b flex items-center gap-2 text-xs"><b>{d.dept}</b><span className="text-muted-foreground">current <b className="num text-foreground">{d.current}</b> / required <b className="num text-foreground">{d.required}</b></span><span className={`ml-auto font-semibold ${d.gap > 0 ? "text-red-600" : "text-emerald-700"}`}>{d.gap > 0 ? `gap ${d.gap}` : d.gap < 0 ? `+${-d.gap}` : "full"}</span></div>
              <table className="w-full text-xs">
                <thead><tr className="border-b text-[10px] uppercase tracking-wider text-muted-foreground"><th className="px-3 py-1 text-left font-semibold">Designation</th><th className="px-2 py-1 text-right font-semibold whitespace-nowrap">Cur / Req</th><th className="px-3 py-1 text-right font-semibold">Gap</th></tr></thead>
                <tbody>
                  {d.rows.map((r) => (
                    <tr key={r.designation} className={`border-b last:border-0 ${r.gap > 0 ? "bg-red-50/60" : ""}`}>
                      <td className="px-3 py-1 text-muted-foreground">{r.designation}{r.remarks && <div className="text-[10px] italic">{r.remarks}</div>}</td>
                      <td className="px-2 py-1 num text-right">{r.current}/{r.required}</td>
                      <td className={`px-3 py-1 num text-right w-12 ${r.gap > 0 ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>{r.gap}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {w.departments.length === 0 && <div className="text-xs text-muted-foreground">No service department rows for this workshop's outlets in the HR upload.</div>}
        </div>
        {w.branches.length > 1 && <div className="mt-3 text-[11px] text-muted-foreground">Outlets: {w.branches.map((b) => `${b.display} ${b.current}/${b.required}`).join(" · ")}</div>}
      </td>
    </tr>
  );
}

export default function ServiceManpower() {
  const [data, setData] = useState(null);
  const [shared, setShared] = useState(false);
  const [open, setOpen] = useState({});
  useEffect(() => {
    api.get(`/service/manpower?${qs({ include_shared: shared })}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
  }, [shared]);
  const t = data?.totals;
  return (
    <div className="space-y-5" data-testid="service-manpower">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2"><Users size={22} /> Service Manpower</h1>
          <p className="text-sm text-muted-foreground">Required vs current headcount in the service departments (Service, Body & Paint, Parts) of each workshop's outlets — from the HR manpower upload</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={shared} onCheckedChange={setShared} data-testid="svc-mp-shared" /> include shared departments (Customer Care, HR, Accounts…)</label>
      </div>
      {t && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          {[["Required", t.required, ""], ["Current", t.current, ""], ["Gap", t.gap, t.gap > 0 ? "text-red-600" : "text-emerald-700"], ["Filled", t.availability != null ? `${t.availability}%` : "—", availTone(t.availability)]].map(([l, v, tone]) => (
            <div key={l} className="border bg-card rounded-md p-4" data-testid={`svc-mp-kpi-${l.toLowerCase()}`}><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{l}</div><div className={`font-heading text-2xl font-extrabold num mt-1 ${tone}`}>{v}</div></div>
          ))}
        </div>
      )}
      <div className="border bg-card rounded-md overflow-auto">
        <table className="w-full text-sm">
          <thead className="border-b"><tr>{["", "Workshop", "Zone", "Required", "Current", "Gap", "Filled"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {(data?.rows || []).map((w) => [
              <tr key={w.code} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => setOpen({ ...open, [w.code]: !open[w.code] })} data-testid={`svc-mp-row-${w.code}`}>
                <td className="px-3 py-2 text-muted-foreground">{open[w.code] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                <td className="px-3 py-2 font-medium whitespace-nowrap">{w.code} <span className="text-muted-foreground font-normal">{w.name}</span> <Link to={`/service/workshop/${w.code}`} onClick={(e) => e.stopPropagation()} className="ml-2 text-[10px] underline text-muted-foreground">dashboard</Link></td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{w.zone || "—"}</td>
                <td className="px-3 py-2 num">{w.required}</td>
                <td className="px-3 py-2 num">{w.current}</td>
                <td className={`px-3 py-2 num font-bold ${w.gap > 0 ? "text-red-600" : "text-emerald-700"}`}>{w.gap > 0 ? `−${w.gap}` : w.gap < 0 ? `+${-w.gap}` : 0}</td>
                <td className="px-3 py-2 w-48"><div className="flex items-center gap-2"><div className="flex-1"><FillBar availability={w.availability} /></div><span className={`num text-xs font-semibold w-12 text-right ${availTone(w.availability)}`}>{w.availability != null ? `${w.availability}%` : "—"}</span></div></td>
              </tr>,
              open[w.code] && <Detail key={`${w.code}-d`} w={w} />,
            ])}
          </tbody>
          {t && (
            <tfoot className="border-t bg-secondary/40 font-semibold text-sm"><tr data-testid="svc-mp-total"><td /><td className="px-3 py-2" colSpan={2}>Group</td><td className="px-3 py-2 num">{t.required}</td><td className="px-3 py-2 num">{t.current}</td><td className={`px-3 py-2 num ${t.gap > 0 ? "text-red-600" : "text-emerald-700"}`}>{t.gap}</td><td className={`px-3 py-2 num ${availTone(t.availability)}`}>{t.availability != null ? `${t.availability}%` : "—"}</td></tr></tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
